import {
  AUC_COLOR,
  COMMON_PLOT_CONFIG,
  HIPHEN_GREEN,
  THEME,
} from "../../../constants";
import {
  selectControlVarieties,
  selectFilteredFeaturesByDate,
  selectNumericalPropertiesMetricsAndAggregatedFeatures,
} from "../../../selectors/resultMap";
import { slideExportConfig, traitLabel } from "./slideExport";
import { useMemo, useState } from "react";

import { BreederTimeline } from "../Timeline/BreederTimeline";
import { Button } from "reactstrap";
import { Chart } from "./Chart";
import Plot from "react-plotly.js";
import PropTypes from "prop-types";
import { PropertyName } from "../../../components/PropertyName/PropertyName";
import chroma from "chroma-js";
import { groupByKey } from "../../../utils";
import { mean } from "lodash";
import { useSelector } from "react-redux";

const PLOT_STYLE = { width: "100%", height: "100%" };

// BreederTimeline insets its dots by half an AUC thumb on each side, and is
// three 15-18px rows tall (dots, months, label).
const TIMELINE_INSET = 7;
const TIMELINE_HEIGHT = 48;

// TODO: Optimize this after featuresByDate is refactored CLOV-392
const prepareData = (
  aggregatedFeatures,
  featuresByDate,
  controlVarieties,
  focusedElements,
  trait
) => {
  const displayData = [];
  const trueTrait = trait.isAddon ? trait.baseTrait : trait;

  let highestVarietyOnAuc;
  const plottedGroups = new Set();

  focusedElements.forEach((focusedElement) => {
    // Resolve a plot (displayId) to its group via the fixed history, not the
    // date-dependent aggregatedFeatures — else the curve shifts/vanishes by date.
    const element =
      featuresByDate.find(({ displayId }) => displayId === focusedElement)
        ?.group ?? focusedElement;

    if (plottedGroups.has(element)) return;
    plottedGroups.add(element);

    // aggregatedFeatures (current date) only needed for the AUC mean below.
    const groupEntry = aggregatedFeatures.find(
      ({ group }) => group === element
    );

    // Determine highest auc mean curve for a specific display
    if (
      trait.isAddon &&
      groupEntry &&
      (!highestVarietyOnAuc ||
        groupEntry.properties[trait.technical_name].mean >
          highestVarietyOnAuc.properties[trait.technical_name].mean)
    )
      highestVarietyOnAuc = groupEntry;

    const features = groupByKey(
      featuresByDate.filter(
        (feature) =>
          feature.group === element &&
          feature.properties[trueTrait.technical_name] != null
      ),
      "date"
    );

    const isControl = controlVarieties.includes(element);

    displayData.push({
      type: "scattergl",
      mode: "lines+markers",
      fill: "none",
      meta: element,
      opacity: 1,
      ...(isControl // If control variety: legend is bold and line is thicker
        ? {
            name: `<b>${element}</b>`,
            line: {
              width: 4,
            },
            marker: {
              size: 10,
            },
          }
        : {
            name: element,
            line: {
              width: 2,
            },
            marker: {
              size: 6,
            },
          }),
      x: Object.keys(features).sort(),
      y: Object.keys(features)
        .sort()
        .map((date) =>
          mean(
            features[date].map(
              (value) => value.properties[trueTrait.technical_name]
            )
          )
        ),
      hovertemplate: `Mean of ${trueTrait.name}: <b>%{y}</b><br>%{meta} on <b>%{x}</b><extra></extra>`,
    });
  });

  // If trait is AUC trait, show area under curve under highest curve
  if (highestVarietyOnAuc) {
    const highestTrace = displayData.find(
      ({ meta }) => meta === highestVarietyOnAuc.group
    );

    if (highestTrace) {
      highestTrace.fill = "tozeroy";
      highestTrace.fillcolor = chroma(AUC_COLOR).alpha(0.4).hex();
    }
  }
  return displayData;
};

/* Evolution of the focused varieties over the flights, with the page's dates
 * bar (BreederTimeline) right under its plot area: the bar is placed on the X
 * axis, so each flight dot sits under its date.
 */
export const EvolutionChart = ({
  trait,
  aucModeChecked,
  aucDates,
  aucDatesIndexes,
  setAucDatesIndexes,
  dates,
  exportName,
}) => {
  const [refreshingFeaturesByDate, focusedVarieties, selectedDate] =
    useSelector(({ resultMap }) => [
      resultMap.refreshingFeaturesByDate,
      resultMap.focusedVarieties,
      resultMap.trial_date,
    ]);

  const featuresByDate = useSelector(selectFilteredFeaturesByDate);
  const [aggregatedPrefilteredFeatures] = useSelector(
    selectNumericalPropertiesMetricsAndAggregatedFeatures
  );

  const controlVarieties = useSelector(selectControlVarieties);

  // Plotly sizes the legend on its longest name: with long variety names it
  // eats the plot, so it can be folded away.
  const [showLegend, setShowLegend] = useState(true);

  const displayData = useMemo(
    () =>
      prepareData(
        aggregatedPrefilteredFeatures,
        featuresByDate,
        controlVarieties,
        focusedVarieties,
        trait
      ),
    [
      focusedVarieties,
      trait,
      featuresByDate,
      controlVarieties,
      aggregatedPrefilteredFeatures,
    ]
  );

  /*
   * Compute layout to include shapes for AUC and current date
   */
  const layout = useMemo(() => {
    const shapes = [
      // Current date
      {
        type: "line",
        x0: selectedDate,
        x1: selectedDate,
        y0: 0,
        y1: 1,
        yref: "paper",
        line: {
          color: HIPHEN_GREEN,
          width: 2,
        },
      },
    ];
    // Rectangle for AUC selection
    const aucLines = aucModeChecked
      ? aucDates.map((date) => {
          return {
            type: "line",
            x0: date,
            y0: 0,
            x1: date,
            yref: "paper",
            y1: 1,
            line: {
              color: AUC_COLOR,
              width: 4,
              dash: "dot",
            },
          };
        })
      : [];

    shapes.push(...aucLines);

    // The axis spans the flights of the dates bar, a little padded so the
    // first and last points are not cut: with or without curves, every
    // flight dot has its date on the axis.
    const first = dates.length ? new Date(dates[0]).getTime() : null;
    const last = dates.length
      ? new Date(dates[dates.length - 1]).getTime()
      : null;
    const pad = Math.max((last - first) * 0.02, 12 * 3600 * 1000);

    return {
      shapes: shapes,
      autosize: true,
      showlegend: showLegend,
      legend: { font: { size: 11.5 } },
      // The dates bar is laid in the bottom margin.
      margin: { l: 40, r: 30, t: 0, b: TIMELINE_HEIGHT },
      plot_bgcolor: "transparent",
      paper_bgcolor: "white",
      yaxis: {
        gridcolor: THEME.indicators,
        tickfont: { size: 11.5 },
        fixedrange: true,
      },
      // The months are written by the dates bar under the plot. Typed: with
      // no variety picked there is no trace to infer it from, and a linear
      // axis put every flight on the same pixel.
      xaxis: {
        type: "date",
        gridcolor: THEME.indicators,
        showticklabels: false,
        fixedrange: true,
        ...(first != null && { range: [first - pad, last + pad] }),
      },

      font: { color: THEME.indicators, size: 13 },

      colorway: THEME.extendedPalette,
    };
  }, [aucDates, aucModeChecked, selectedDate, dates, showLegend]);

  // The slide export (slideExport.js), with the months back on the X axis:
  // on screen the dates bar writes them, and it is HTML.
  const config = useMemo(
    () => ({
      ...COMMON_PLOT_CONFIG,
      ...slideExportConfig({
        title: `Evolution of focused varieties: mean of ${traitLabel(
          trait.isAddon ? trait.baseTrait : trait
        )}`,
        filename: exportName,
        layout: ({ margin, xaxis }) => ({
          margin: { ...margin, t: 40, b: 30 },
          xaxis: { ...xaxis, showticklabels: true, tickfont: { size: 11.5 } },
        }),
      }),
    }),
    [exportName, trait]
  );

  // Where the first and last flights land on the X axis and where the plot
  // area ends, in pixels from the plot's top left corner. Read after each draw
  // and resize (the legend width changes the plot area), from Plotly's own
  // layout and axis conversion.
  const [axisSpan, setAxisSpan] = useState(null);
  const measureAxis = (_figure, graphDiv) => {
    const { xaxis, _size } = graphDiv._fullLayout ?? {};
    if (!xaxis || !dates.length) return;
    const left = Math.round(_size.l + xaxis.d2p(dates[0]));
    const right = Math.round(_size.l + xaxis.d2p(dates[dates.length - 1]));
    const top = Math.round(_size.t + _size.h);
    setAxisSpan((span) =>
      span?.left === left && span?.right === right && span?.top === top
        ? span
        : { left, right, top }
    );
  };

  return (
    <Chart
      icon="fa-solid fa-chart-line"
      title={
        trait.technical_name && (
          <span>
            Evolution of focused varieties: mean of{" "}
            <PropertyName
              className="breeder-selected-trait"
              showIcon
              showUnit
              technicalName={
                (trait.isAddon ? trait.baseTrait : trait).technical_name
              }
            />
          </span>
        )
      }
      topRightContent={
        focusedVarieties.length > 0 && (
          <Button
            size="sm"
            color="link"
            className="evolution-legend-btn"
            onClick={() => setShowLegend(!showLegend)}
          >
            <i className="fa-solid fa-list me-1" />
            {showLegend ? "Hide legend" : "Show legend"}
          </Button>
        )
      }
      isLoading={refreshingFeaturesByDate}
      missingData={featuresByDate.length === 0 && !refreshingFeaturesByDate}
    >
      {/* Always drawn, even without varieties: the dates bar under it is how
          the date is changed. */}
      <div className="evolution-plot">
        <div className="evolution-plot-area">
          <Plot
            useResizeHandler
            data={displayData}
            layout={layout}
            config={config}
            onInitialized={measureAxis}
            onUpdate={measureAxis}
            style={PLOT_STYLE}
          />
          {axisSpan && dates.length > 0 && (
            <div
              className="evolution-timeline"
              style={{
                top: axisSpan.top,
                left: axisSpan.left - TIMELINE_INSET,
                width: axisSpan.right - axisSpan.left + 2 * TIMELINE_INSET,
              }}
            >
              <BreederTimeline
                dates={dates}
                selectedDate={selectedDate}
                aucModeChecked={aucModeChecked}
                aucDatesIndexes={aucDatesIndexes}
                setAucDatesIndexes={setAucDatesIndexes}
              />
            </div>
          )}
          {focusedVarieties.length === 0 && (
            <div className="evolution-plot-empty">
              <i className="fa-solid fa-magnifying-glass" /> Use the selection
              tool to select genotypes
            </div>
          )}
        </div>
      </div>
    </Chart>
  );
};

EvolutionChart.propTypes = {
  aucModeChecked: PropTypes.bool.isRequired,
  aucDates: PropTypes.array.isRequired,
  aucDatesIndexes: PropTypes.array.isRequired,
  setAucDatesIndexes: PropTypes.func.isRequired,
  dates: PropTypes.array.isRequired,
  exportName: PropTypes.string.isRequired,
  trait: PropTypes.object,
};
